import React from "react";
import styles from "./machine-overview-container.module.scss";
import cn from "classnames";
import Spinner from "@src/components/atoms/spinner/spinner";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { useMachineOverview } from "./use-machine-overview";
import { useI18N, useLanguage } from "@src/language/language-context";
import type {
  ModeColor,
  MachineOverviewDefinition,
} from "@src/types/factory-dashboard";

type GridArea = "quarter" | "half";

interface MachineOverviewContainerProps {
  machine: MachineOverviewDefinition;
  gridArea: GridArea;
}

const cssClassName = "machine-overview-container";

const getModeBadgeStyle = (modeColor: ModeColor) => ({
  backgroundColor: modeColor.background,
  borderColor: modeColor.outline,
  color: modeColor.outline,
  outline: `1px solid ${modeColor.outline}`,
});

const formatInteger = (value: number, locale: string) =>
  value.toLocaleString(locale);

export const MachineOverviewContainer = ({
  machine,
  gridArea,
}: MachineOverviewContainerProps) => {
  const i18n = useI18N();
  const language = useLanguage();
  const locale = language ?? "en";
  const {
    machineTitle,
    isLoading,
    hasData,
    modeLabel,
    modeColor,
    cyclesDone,
    cycleLimit,
    progress,
    animatedProgressPercentage,
    productionDataset,
    moldCatActualDescription,
    filename,
    customer,
    material,
    lastUpdatedTime,
  } = useMachineOverview(machine);

  const hasProductionDatasetField = Boolean(
    machine.nodes.productionDatasetName,
  );
  const hasMoldCatActualDescriptionField = Boolean(
    machine.nodes.moldCatActualDescription,
  );
  const hasFilenameField = Boolean(machine.nodes.filename);
  const hasCustomerField = Boolean(machine.nodes.customer);
  const hasMaterialField = Boolean(machine.nodes.material);

  const texts = {
    cycles: i18n("modules.machineOverviewContainer.cycles"),
    progress: i18n("modules.machineOverviewContainer.progress"),
    productionDataset: i18n(
      "modules.machineOverviewContainer.productionDataset",
    ),
    moldCatActualDescription: i18n(
      "modules.machineOverviewContainer.moldCatActualDescription",
    ),
    filename: i18n("modules.machineOverviewContainer.filename"),
    lastUpdated: i18n("modules.machineOverviewContainer.lastUpdated"),
    customer: i18n("modules.machineOverviewContainer.customer"),
    material: i18n("modules.machineOverviewContainer.material"),
    noDataAvailable: i18n("modules.machineOverviewContainer.noDataAvailable"),
  };

  return (
    <section
      className={cn(styles[cssClassName], {
        [styles[`${cssClassName}--grid-quarter`]]: gridArea === "quarter",
        [styles[`${cssClassName}--grid-half`]]: gridArea === "half",
        [styles[`${cssClassName}--loading`]]: isLoading,
      })}
    >
      {isLoading ? (
        <div className={styles[`${cssClassName}__spinner-wrapper`]}>
          <Spinner />
        </div>
      ) : !hasData ? (
        <>
          <h3 className={styles[`${cssClassName}__title`]}>{machineTitle}</h3>
          <div className={styles[`${cssClassName}__no-data`]}>
            <p className={styles[`${cssClassName}__no-data-text`]}>
              {texts.noDataAvailable}
            </p>
          </div>
        </>
      ) : (
        <>
          <h3 className={styles[`${cssClassName}__title`]}>{machineTitle}</h3>

          <button
            className={styles[`${cssClassName}__badge`]}
            style={getModeBadgeStyle(modeColor)}
            type="button"
          >
            {modeLabel}
          </button>

          <div className={styles[`${cssClassName}__row`]}>
            <span>{texts.cycles}</span>
            <span className={styles[`${cssClassName}__value`]}>
              {formatInteger(cyclesDone, locale)} /{" "}
              {formatInteger(cycleLimit, locale)}
            </span>
          </div>

          <div className={styles[`${cssClassName}__progress-track`]}>
            <div
              className={styles[`${cssClassName}__progress-fill`]}
              style={{ width: `${animatedProgressPercentage}%` }}
            />
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span>{texts.progress}</span>
            <span className={styles[`${cssClassName}__value`]}>
              {progress.toFixed(1)}%
            </span>
          </div>

          {hasCustomerField && (
            <div className={styles[`${cssClassName}__row`]}>
              <span>{texts.customer}</span>
              <span className={styles[`${cssClassName}__value`]}>
                {customer}
              </span>
            </div>
          )}

          {hasProductionDatasetField && (
            <div className={styles[`${cssClassName}__row`]}>
              <span>{texts.productionDataset}</span>
              <span className={styles[`${cssClassName}__value`]}>
                {productionDataset}
              </span>
            </div>
          )}

          {hasMaterialField && (
            <div className={styles[`${cssClassName}__row`]}>
              <span>{texts.material}</span>
              <span className={styles[`${cssClassName}__value`]}>
                {material}
              </span>
            </div>
          )}

          {hasMoldCatActualDescriptionField && (
            <div className={styles[`${cssClassName}__row`]}>
              <span>{texts.moldCatActualDescription}</span>
              <span className={styles[`${cssClassName}__value`]}>
                {moldCatActualDescription}
              </span>
            </div>
          )}

          {hasFilenameField && (
            <div className={styles[`${cssClassName}__row`]}>
              <span>{texts.filename}</span>
              <span className={styles[`${cssClassName}__value`]}>
                {filename}
              </span>
            </div>
          )}
        </>
      )}

      {lastUpdatedTime && (
        <p className={styles[`${cssClassName}__updated-at`]}>
          {texts.lastUpdated}: <TimezoneDate date={lastUpdatedTime} />
        </p>
      )}
    </section>
  );
};
